import React, { useEffect, useRef, useState } from "react";
import { Microphone, MicrophoneSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

// One-time discoverability nudge so first-time users notice the mic
// can talk for them. Shown ONCE per device, then dismissed forever.
const FIRST_USE_KEY = "hw:dictation:hint-seen";

// Talk-to-text dictation for any textarea / input.
//
// Why this exists (when phones already have a keyboard mic):
//   • Desktop / web browsers have NO native dictation in the keyboard.
//   • Many users (especially older ones) don't know about the
//     keyboard mic icon. A big visible button is way more discoverable.
//   • Hands-free worship moments — pray with eyes closed, dictate
//     without fumbling with the keyboard.
//
// Implementation: Web Speech API. Built into Chrome, Safari, Edge,
// every modern browser. Zero cost, zero API keys, no limits.
//
// Usage:
//   <DictationButton onTranscript={(t) => setText(prev => prev + t)} />
//
// Behavior:
//   • Tap once = start listening. Interim transcript shown live.
//   • Tap again = stop. Final transcript pushed to onTranscript.
//   • Auto-stops after 3s of silence.
//   • Pauses the global VoiceCommander wake-word listener while
//     dictation is active so the two don't fight for the mic.
export default function DictationButton({
  onTranscript,
  // Fires with the final transcript when dictation stops — lets hosts
  // auto-send the message ("talk to AI" without an extra tap).
  onDone,
  size = 36,
  className = "",
  testId = "dictation-button",
  label = "Speak your message",
  // Where to render the live transcript bubble. "above" floats above
  // the button (best for textareas), "inline" appears next to it.
  bubblePlacement = "above",
  // `bare` renders the mic as a flat icon (no filled circle / ring / shadow)
  // so it blends with neighboring composer icons instead of dominating them.
  bare = false,
}) {
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  // First-visit hint bubble: appears once per device above the mic so
  // people actually realize they can speak instead of type. Suppressed
  // forever after the first tap on the mic OR the bubble itself.
  const [showHint, setShowHint] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return !window.localStorage.getItem(FIRST_USE_KEY); } catch (_) { return false; }
  });
  const dismissHint = () => {
    setShowHint(false);
    try { window.localStorage.setItem(FIRST_USE_KEY, "1"); } catch (_) {}
  };
  const recRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const finalChunkRef = useRef("");

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    rec.onresult = (event) => {
      // Rebuild the full transcript from scratch on every event (replace,
      // never accumulate). Some mobile engines (Samsung/Android Chrome)
      // re-emit already-final results with a non-advancing resultIndex;
      // a "+=" accumulator turns "Amos" into "Amos Amos Amos". Iterating
      // all results and replacing avoids that entirely.
      let finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        const t = (r[0] && r[0].transcript) || "";
        if (r.isFinal) finalText += (finalText ? " " : "") + t.trim();
        else interimText += t;
      }
      finalChunkRef.current = finalText;
      if (finalText) {
        // Reset silence timer when we get any final result.
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(() => stop(true), 3000);
      }
      setInterim(interimText);
    };
    rec.onerror = (e) => {
      // Not-allowed = user denied mic permission. Silence = no speech
      // detected (auto-restart). Everything else = abort gracefully.
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone permission denied.");
        setListening(false);
        return;
      }
      if (e.error === "no-speech" || e.error === "audio-capture") {
        // Recoverable — let onend handle the restart logic.
        return;
      }
    };
    rec.onend = () => {
      // Auto-restart if still in listening state (Web Speech API
      // sometimes ends after ~60s of silence even with continuous=true).
      // Read the flag off the instance — the closure's `listening` is
      // frozen at mount and would never restart.
      if (rec.__listening) {
        try { rec.start(); } catch (_) { /* already running */ }
      }
    };
    recRef.current = rec;
    return () => {
      try { rec.stop(); } catch (_) {}
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep `listening` fresh inside the onend callback.
  useEffect(() => {
    if (!recRef.current) return;
    recRef.current.__listening = listening;
  }, [listening]);

  const start = () => {
    if (!recRef.current) return;
    finalChunkRef.current = "";
    setInterim("");
    try {
      recRef.current.start();
      setListening(true);
      // Tell the global voice commander to pause its wake-word
      // listener so we don't double-capture audio.
      try { window.dispatchEvent(new CustomEvent("hw:voice:pause")); } catch (_) {}
      // Auto-stop after 3s of silence (the onresult handler resets this).
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => stop(true), 12000);
    } catch (e) {
      // Already running — toggle to stop instead.
      stop(true);
    }
  };

  const stop = (push = true) => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (_) {}
    setListening(false);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    // Resume the global voice commander.
    try { window.dispatchEvent(new CustomEvent("hw:voice:resume")); } catch (_) {}
    if (push && (finalChunkRef.current || interim)) {
      let text = (finalChunkRef.current + (interim ? " " + interim : "")).trim();
      // Mobile speech engines sometimes repeat a word across re-emitted
      // result segments ("Amos Amos Amos"). Collapse immediate duplicate
      // words (case-insensitive) so dictation reads cleanly.
      text = text.replace(/\b(\w+)(?:\s+\1\b)+/gi, "$1");
      if (text) {
        onTranscript?.(" " + text);
        onDone?.(text);
      }
    }
    setInterim("");
    finalChunkRef.current = "";
  };

  const toggle = () => {
    if (showHint) dismissHint();
    return listening ? stop(true) : start();
  };

  if (!supported) {
    return null; // Hide entirely on browsers that can't do it (older Firefox)
  }

  // Flat (bare) mics use a smaller icon to match neighboring composer icons.
  const iconSize = bare ? Math.round(size * 0.42) : Math.round(size * 0.5);

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={testId}
        aria-label={listening ? "Stop dictation" : label}
        title={listening ? "Tap to stop" : label}
        className={`relative inline-flex items-center justify-center rounded-full transition-all ${
          bare
            ? (listening ? "text-terracotta animate-pulse" : "text-cream/60 hover:text-terracotta")
            : `shadow-sm ${listening
                ? "bg-terracotta text-cream ring-4 ring-terracotta/30 animate-pulse"
                : "bg-cream text-forest hover:bg-forest hover:text-cream border border-forest/15"}`
        }`}
        style={{ width: size, height: size }}
      >
        {listening ? <MicrophoneSlash size={iconSize} weight="fill" /> : <Microphone size={iconSize} weight="fill" />}
      </button>
      {listening && (interim || true) && (
        <div
          className={`absolute z-50 pointer-events-none select-none ${
            bubblePlacement === "above"
              ? "bottom-full mb-2 left-0"
              : "left-full ml-2 top-1/2 -translate-y-1/2"
          }`}
          style={{ minWidth: 200, maxWidth: 320 }}
        >
          <div className="px-3 py-2 rounded-xl bg-forest text-cream text-xs leading-snug shadow-2xl">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Listening…</span>
            </div>
            <div className="text-cream/95 line-clamp-3">{interim || "speak now"}</div>
          </div>
        </div>
      )}
    </div>
  );
}
